import React, { useState } from 'react';
import DateInput from './DateInput';
import useEscapeKey from '../hooks/useEscapeKey';

const ADDITION_TYPES = [
  { value: 'salary_advance', label: 'Salary Advance' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'expense_reimbursement', label: 'Expense Reimbursement' },
  { value: 'car_allowance', label: 'Car Allowance' },
  { value: 'kit_days', label: 'Keeping in Touch Days (KIT)' },
  { value: 'other', label: 'Other' },
];

// A cash car allowance and (non-taxable) expense reimbursements aren't normally
// pensionable; most other additions are. This is just the starting default for the
// checkbox — the user can override it per addition.
function defaultPensionable(additionType) {
  return !(additionType === 'car_allowance' || additionType === 'expense_reimbursement');
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Rolling 24 months from current month — used for the salary-advance recovery picker.
function generateFutureMonths() {
  const now = new Date();
  const months = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
      shortLabel: `${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${String(d.getFullYear()).slice(2)}`,
    });
  }
  return months;
}

export default function AdditionModal({ employee, onSave, onClose, activeParentalLeaves = [] }) {
  useEscapeKey(onClose);
  const futureMonths = generateFutureMonths();

  // Most-correct KIT daily rate: annual salary ÷ the employee's contracted working
  // days per year (days/week × 52). qualifying_days_per_week defaults to 5, so a
  // full-time Mon–Fri worker resolves to the standard annual ÷ 260, while a 3-day
  // part-timer resolves to annual ÷ 156 — keeping their per-day rate accurate rather
  // than diluting an already pro-rated salary across a notional 5-day week.
  // Salaried only (hourly staff have no annual salary). Editable: KIT pay isn't
  // legally fixed, so this is a starting point the client can override.
  const kitDaysPerWeek = parseInt(employee.qualifying_days_per_week, 10) || 5;
  const kitAnnualDivisor = kitDaysPerWeek * 52;
  const suggestedKitDailyRate =
    employee.pay_type !== 'hourly' && parseFloat(employee.annual_salary) > 0
      ? (parseFloat(employee.annual_salary) / kitAnnualDivisor).toFixed(2)
      : '';

  const [form, setForm] = useState({
    addition_type: 'salary_advance',
    amount: '',
    effective_date: '',
    recurring: false,
    recurring_end_date: '',
    notes: '',
    // Whether this addition counts toward pensionable pay. Default depends on type.
    pensionable: defaultPensionable('salary_advance'),
    // Free-text name for an "Other" addition (e.g. a named allowance the
    // dropdown doesn't cover). Required when addition_type === 'other'.
    other_description: '',
    // KIT days fields
    kit_daily_rate: suggestedKitDailyRate,
    kit_number_of_days: '',
    kit_selected_dates: [],
    kit_month: new Date().getMonth(),
    kit_year: new Date().getFullYear(),
    kit_parental_leave_id: activeParentalLeaves[0]?.id || '',
    // Salary advance recovery schedule
    recovery_months: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isKitDays = form.addition_type === 'kit_days';
  const isSalaryAdvance = form.addition_type === 'salary_advance';
  const isExpenseReimbursement = form.addition_type === 'expense_reimbursement';
  const isOther = form.addition_type === 'other';
  const isCarAllowance = form.addition_type === 'car_allowance';

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setError('');
    setForm(prev => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      // Reset the pensionable default whenever the addition type changes.
      if (name === 'addition_type') next.pensionable = defaultPensionable(value);
      return next;
    });
  };

  const toggleKitDate = (dateStr) => {
    setForm(prev => {
      const dates = prev.kit_selected_dates.includes(dateStr)
        ? prev.kit_selected_dates.filter(d => d !== dateStr)
        : [...prev.kit_selected_dates, dateStr].sort();
      return { ...prev, kit_selected_dates: dates, kit_number_of_days: String(dates.length) };
    });
  };

  const toggleRecoveryMonth = (monthKey) => {
    setForm(prev => {
      const months = prev.recovery_months.includes(monthKey)
        ? prev.recovery_months.filter(m => m !== monthKey)
        : [...prev.recovery_months, monthKey].sort();
      return { ...prev, recovery_months: months };
    });
  };

  const navigateKitMonth = (delta) => {
    setForm(prev => {
      let m = prev.kit_month + delta;
      let y = prev.kit_year;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      return { ...prev, kit_month: m, kit_year: y };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isKitDays) {
      if (form.kit_selected_dates.length === 0) {
        setError('Please select at least one KIT day');
        return;
      }
      if (!form.kit_daily_rate || parseFloat(form.kit_daily_rate) <= 0) {
        setError('Please enter a daily pay rate');
        return;
      }
      if (activeParentalLeaves.length > 0 && !form.kit_parental_leave_id) {
        setError('Pick which parental leave these KIT days belong to');
        return;
      }
    } else {
      if (!form.amount || parseFloat(form.amount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }
    }
    if (!form.addition_type) {
      setError('Please select an addition type');
      return;
    }
    if (isOther && !form.other_description.trim()) {
      setError('Please describe what this "Other" addition is');
      return;
    }
    setSaving(true);
    try {
      const saveData = isKitDays
        ? {
            addition_type: form.addition_type,
            number_of_days: form.kit_selected_dates.length,
            selected_dates: form.kit_selected_dates,
            daily_rate: parseFloat(form.kit_daily_rate).toFixed(2),
            amount: (form.kit_selected_dates.length * parseFloat(form.kit_daily_rate)).toFixed(2),
            notes: form.notes,
            addition_type_label: 'Keeping in Touch Days (KIT)',
            parental_leave_id: form.kit_parental_leave_id || null,
            pensionable: true,
          }
        : {
            ...form,
            amount: parseFloat(form.amount).toFixed(2),
            // For "Other", the user's free-text name becomes the label so it
            // reads as e.g. "Car allowance: £500" everywhere additions display,
            // rather than the bare word "Other".
            addition_type_label: isOther
              ? form.other_description.trim()
              : (ADDITION_TYPES.find(t => t.value === form.addition_type)?.label || form.addition_type),
            other_description: isOther ? form.other_description.trim() : undefined,
            recurring_end_date: form.recurring && form.recurring_end_date ? form.recurring_end_date : null,
            recovery_months: isSalaryAdvance ? form.recovery_months : undefined,
          };
      // Remove internal KIT fields from non-KIT saves
      delete saveData.kit_number_of_days;
      delete saveData.kit_selected_dates;
      delete saveData.kit_month;
      delete saveData.kit_year;
      delete saveData.kit_parental_leave_id;
      if (!isSalaryAdvance) delete saveData.recovery_months;

      await onSave(employee.id, saveData);
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // Build calendar grid for KIT day picker
  const daysInMonth = getDaysInMonth(form.kit_year, form.kit_month);
  const firstDayOfWeek = new Date(form.kit_year, form.kit_month, 1).getDay(); // 0=Sun
  const calendarDays = [];
  for (let i = 0; i < firstDayOfWeek; i++) calendarDays.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarDays.push(d);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Addition - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Addition Type *</label>
              <select name="addition_type" value={form.addition_type} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                {ADDITION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {isOther && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description *</label>
                <input type="text" name="other_description" value={form.other_description} onChange={handleChange}
                  maxLength={100}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="e.g. Car allowance" />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Name this addition so it's clear on the payroll submission. Treated as taxable pay (subject to PAYE &amp; NI).
                </p>
              </div>
            )}

            {isKitDays ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select KIT Days *</label>
                  <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <button type="button" onClick={() => navigateKitMonth(-1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        {MONTH_NAMES[form.kit_month]} {form.kit_year}
                      </span>
                      <button type="button" onClick={() => navigateKitMonth(1)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center">
                      {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                        <div key={d} className="text-xs font-medium text-gray-400 py-1">{d}</div>
                      ))}
                      {calendarDays.map((day, idx) => {
                        if (day === null) return <div key={`empty-${idx}`} />;
                        const dateStr = `${form.kit_year}-${String(form.kit_month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        const isSelected = form.kit_selected_dates.includes(dateStr);
                        const dayOfWeek = new Date(form.kit_year, form.kit_month, day).getDay();
                        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() => toggleKitDate(dateStr)}
                            className={`text-xs py-1.5 rounded transition-colors ${
                              isSelected
                                ? 'bg-brand-600 text-white font-semibold'
                                : isWeekend
                                  ? 'text-gray-300 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-brand-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {form.kit_selected_dates.length > 0 && (
                    <p className="text-sm text-brand-700 dark:text-brand-400 mt-2 font-medium">
                      {form.kit_selected_dates.length} day{form.kit_selected_dates.length !== 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                {activeParentalLeaves.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Linked Parental Leave *</label>
                    <select name="kit_parental_leave_id" value={form.kit_parental_leave_id} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                      <option value="">— select parental leave —</option>
                      {activeParentalLeaves.map(pl => (
                        <option key={pl.id} value={pl.id}>
                          {pl.leave_type ? pl.leave_type.replace('_', ' ') : 'Parental leave'}
                          {pl.start_date ? ` from ${new Date(pl.start_date).toLocaleDateString('en-GB')}` : ''}
                          {typeof pl.kit_days_used === 'number' ? ` · ${pl.kit_days_used}/10 KIT used` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      UK law caps KIT days at 10 per maternity/adoption leave.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Daily Pay Rate *</label>
                  <div className="mt-1 relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                    <input type="number" name="kit_daily_rate" step="0.01" min="0" inputMode="decimal" value={form.kit_daily_rate || ''} onChange={handleChange}
                      className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="0.00" />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {suggestedKitDailyRate
                      ? `Suggested from salary — a normal day's pay based on ${kitDaysPerWeek} working day${kitDaysPerWeek === 1 ? '' : 's'}/week. Edit if you’ve agreed a different KIT rate.`
                      : 'Pay amount per KIT day (gross)'}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount (gross) *</label>
                  <div className="mt-1 relative">
                    <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                    <input type="number" name="amount" step="0.01" min="0" inputMode="decimal" value={form.amount} onChange={handleChange}
                      className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="0.00" />
                  </div>
                  {!isExpenseReimbursement && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Enter the gross amount before tax and deductions</p>
                  )}
                  {isCarAllowance && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">A cash car allowance is taxable pay — it goes through PAYE and is subject to tax &amp; NI, like salary.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Effective Date</label>
                  <DateInput name="effective_date" value={form.effective_date} onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                </div>

                <label className="flex items-center space-x-2">
                  <input type="checkbox" name="recurring" checked={form.recurring} onChange={handleChange}
                    className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Recurring (applies each pay period until end date or cancelled)</span>
                </label>

                {form.recurring && (
                  <div className="ml-6">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">End date (optional)</label>
                    <DateInput name="recurring_end_date" value={form.recurring_end_date} onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Leave blank if open-ended — you can cancel it later from the dashboard.
                    </p>
                  </div>
                )}

                {!isExpenseReimbursement && (
                  <div>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" name="pensionable" checked={form.pensionable} onChange={handleChange}
                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Include in pensionable pay</span>
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                      {form.pensionable
                        ? 'Pension contributions will be calculated on this amount too.'
                        : 'This amount is excluded from the pension calculation.'}
                    </p>
                  </div>
                )}

                {isSalaryAdvance && (
                  <div className="p-4 bg-amber-50 dark:bg-amber-900/30 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Recovery schedule
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Pick the months you'll deduct this advance back from the employee's pay. The system will auto-add a deduction line in each selected month.
                    </p>
                    <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
                      {futureMonths.map(month => {
                        const isSelected = form.recovery_months.includes(month.key);
                        return (
                          <button
                            key={month.key}
                            type="button"
                            onClick={() => toggleRecoveryMonth(month.key)}
                            className={`text-xs py-2 px-1 rounded-md border transition-colors ${
                              isSelected
                                ? 'bg-amber-600 text-white border-amber-600 font-semibold'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-amber-300'
                            }`}
                          >
                            {month.shortLabel}
                          </button>
                        );
                      })}
                    </div>
                    {form.recovery_months.length > 0 && parseFloat(form.amount) > 0 && (
                      <div className="text-xs text-amber-700 dark:text-amber-400">
                        £{(parseFloat(form.amount) / form.recovery_months.length).toFixed(2)} will be recovered each month over {form.recovery_months.length} month{form.recovery_months.length !== 1 ? 's' : ''}.
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="3" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Any additional details about this addition..." />
            </div>

            <div className="p-3 bg-green-50 dark:bg-green-900/30 rounded text-sm text-green-700 dark:text-green-300">
              {isKitDays
                ? `${form.kit_selected_dates.length} KIT day${form.kit_selected_dates.length !== 1 ? 's' : ''} will be included in the next payroll submission for ${employee.first_name} ${employee.last_name}.`
                : `This addition of \u00A3${form.amount || '0.00'} will be included in the next payroll submission for ${employee.first_name} ${employee.last_name}.`
              }
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Add Addition'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
